/**
 * Companies store (UI state only)
 * Store สำหรับบริษัท (สถานะ UI เท่านั้น)
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { CompanyFilters } from './types'

type ViewMode = 'table' | 'grid'

interface CompaniesState {
  // UI State - persisted
  viewMode: ViewMode
  filters: CompanyFilters

  // Actions
  setViewMode: (mode: ViewMode) => void
  setFilters: (filters: Partial<CompanyFilters>) => void
  resetFilters: () => void
  setSearch: (search: string) => void
  setStatus: (status: CompanyFilters['status']) => void
  setPage: (page: number) => void
  setPageSize: (pageSize: number) => void
}

const defaultFilters: CompanyFilters = {
  search: '',
  status: 'all',
  page: 1,
  pageSize: 10,
}

export const useCompaniesStore = create<CompaniesState>()(
  persist(
    (set) => ({
      // Initial state
      viewMode: 'table',
      filters: defaultFilters,

      // Actions
      setViewMode: (mode) => set({ viewMode: mode }),

      setFilters: (newFilters) =>
        set((state) => ({
          filters: { ...state.filters, ...newFilters, page: 1 },
        })),

      resetFilters: () =>
        set({
          filters: defaultFilters,
        }),

      setSearch: (search) =>
        set((state) => ({
          filters: { ...state.filters, search, page: 1 },
        })),

      setStatus: (status) =>
        set((state) => ({
          filters: { ...state.filters, status, page: 1 },
        })),

      setPage: (page) =>
        set((state) => ({
          filters: { ...state.filters, page },
        })),

      setPageSize: (pageSize) =>
        set((state) => ({
          filters: { ...state.filters, pageSize, page: 1 },
        })),
    }),
    {
      name: 'companies-storage',
      partialize: (state) => ({
        viewMode: state.viewMode,
        filters: state.filters,
      }),
    }
  )
)

// Selector hooks for better performance
export const useCompaniesViewMode = () => useCompaniesStore((state) => state.viewMode)

export const useCompaniesFilters = () => useCompaniesStore((state) => state.filters)

export const useCompaniesSearch = () => useCompaniesStore((state) => state.filters.search)

export const useCompaniesStatus = () => useCompaniesStore((state) => state.filters.status)

export const useCompaniesPage = () => useCompaniesStore((state) => state.filters.page)

export const useCompaniesPageSize = () => useCompaniesStore((state) => state.filters.pageSize)
